package com.evoapps.evoshift.domain.legal

import kotlinx.datetime.LocalDate
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * One country's legal calendar for one year (SH-55): the statutory trading Sundays, the public
 * holidays on which trade is banned, and days with shortened trading hours. Written by us from the
 * statute (firebase/legal/{country}/{year}.json, seeded into `legal_calendars/{country}_{year}` by
 * scripts/legal/seed_legal_calendar.js), never derived in the app: a change in the Act is a data
 * edit, not a release. Every entry carries its legal basis, source and the date it was checked.
 */
@Serializable
data class LegalCalendarYear(
    /** ISO country code ("PL"), as Market.countryCode. */
    val country: String,
    val year: Int,
    val schemaVersion: Int = 1,
    /** When the whole list was last compared with the statute (ISO date). */
    val checkedOn: String = "",
    val note: String = "",
    val sources: List<LegalSource> = emptyList(),
    val crossChecks: List<LegalCrossCheck> = emptyList(),
    /** Sundays on which the trading ban does not apply (PL: art. 7 ust. 1). */
    val tradingSundays: List<LegalDay> = emptyList(),
    /** Public holidays (PL: ustawa o dniach wolnych od pracy art. 1 pkt 1); [LegalDay.tradeBanned] per day. */
    val holidays: List<LegalDay> = emptyList(),
    /** Days with trade allowed only until [LegalDay.tradeUntil] (PL: Holy Saturday, art. 8). */
    val restrictedDays: List<LegalDay> = emptyList()
) {
    val tradingSundayDates: Set<LocalDate> by lazy { tradingSundays.mapNotNull { it.localDate }.toSet() }
    val holidayDates: Set<LocalDate> by lazy { holidays.mapNotNull { it.localDate }.toSet() }

    /** The holiday on [date] whose trade is banned, if any. */
    fun tradeBannedHoliday(date: LocalDate): LegalDay? = holidays.firstOrNull { it.tradeBanned && it.localDate == date }

    fun restrictedDay(date: LocalDate): LegalDay? = restrictedDays.firstOrNull { it.localDate == date }

    /** Entries someone still has to check against the statute. */
    val entriesNeedingReview: List<LegalDay> get() = (tradingSundays + holidays + restrictedDays).filter { it.needsReview }

    companion object {
        private val json = Json { ignoreUnknownKeys = true }

        fun documentId(country: String, year: Int) = "${country}_$year"

        fun parse(text: String): LegalCalendarYear = json.decodeFromString(serializer(), text)
    }
}

@Serializable
data class LegalDay(
    /** ISO date. */
    val date: String,
    val name: String = "",
    /** Article of the statute this day follows from. */
    val basis: String = "",
    val sourceUrl: String = "",
    val checkedOn: String = "",
    /** Not certain yet: listed in the PR and the owner's checklist until somebody confirms it. */
    val needsReview: Boolean = false,
    /** Which rule of the Act produced the date (trading Sundays). */
    val rule: String? = null,
    /** Holidays: whether trade is banned on the day itself. */
    val tradeBanned: Boolean = true,
    /** Restricted days: trade allowed until this local time ("14:00"). */
    val tradeUntil: String? = null
) {
    val localDate: LocalDate? get() = runCatching { LocalDate.parse(date) }.getOrNull()
}

@Serializable
data class LegalSource(
    val id: String,
    val title: String,
    val url: String,
    val isap: String? = null
)

/** A comparison with an official published list (e.g. gov.pl): MATCH, MISMATCH or NOT_PUBLISHED. */
@Serializable
data class LegalCrossCheck(
    val url: String = "",
    val publisher: String = "",
    val checkedOn: String = "",
    val result: String = "",
    val detail: String = ""
)

/** The calendars shipped with the app (the fallback when Firestore has none). */
object LegalCalendars {
    private val parsed: Map<String, LegalCalendarYear> by lazy {
        BundledLegalCalendars.json.mapValues { (_, text) -> LegalCalendarYear.parse(text) }
    }

    fun bundled(country: String, year: Int): LegalCalendarYear? = parsed[LegalCalendarYear.documentId(country, year)]

    /** Every bundled calendar, by document id. */
    val all: Map<String, LegalCalendarYear> get() = parsed
}
